/**
 * Дата платежа, повторение и окончание — один компонент на все формы.
 *
 * Раньше эти поля писались в каждой форме заново, и они разошлись: в настройке
 * был вариант «раз в две недели», в приложении нет, разового платежа не было
 * нигде, а правка платежа не умела менять ритм вовсе — там правилось «какого
 * числа», которое календарь для платежа с расписанием попросту игнорирует.
 *
 * Здесь три поля и одно правило их поведения:
 *
 * · **Дата платежа** одна и та же для любого ритма. Для повторяющегося она
 *   задаёт и день, и фазу: платёж 31-го в феврале переносится на 28-е, а в
 *   марте снова наступает 31-го.
 * · **Повторение** по умолчанию «каждый месяц»: обязательный платёж по природе
 *   повторяется, и разовый по умолчанию тихо удалял бы аренду из расчёта со
 *   следующего месяца.
 * · **Когда закончится** показывается только у повторяющегося: у разового
 *   заканчивать нечего.
 *
 * Получившееся правило пишется словами под полями — человек видит результат,
 * не открывая форму заново.
 */
import { useState, type ReactNode } from 'react';
import {
  REPEAT_OPTIONS, repeatText, scheduleFrom,
  type PlainDate, type RepeatEnd, type RepeatKey,
} from '@skoka/core';
import { Tip } from './tip.js';
import { DateButton } from './date-picker.js';
import { SelectField } from './operation-fields.js';
import { CardDate, CardInput, CardPick } from './card-fields.js';
import { SheetWrap } from './sheet.js';
import { Icon } from './icon.js';
import { dayMonth } from './text.js';

export interface ScheduleDraft {
  /** Дата платежа. Для повторяющегося — первая или любая из ряда. */
  date: string;
  repeat: RepeatKey;
  end: RepeatEnd;
}

const isDate = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Готов ли черновик к сохранению: дата названа, окончание осмысленно. */
export const scheduleReady = (v: ScheduleDraft): boolean => isDate(v.date)
  && (v.repeat === 'never'
    || v.end.kind === 'never'
    || (v.end.kind === 'date' && isDate(v.end.date) && v.end.date >= v.date)
    || (v.end.kind === 'times' && v.end.times >= 1));

/** Расписание из черновика. `from` ставится при правке существующего платежа. */
export const scheduleOf = (v: ScheduleDraft, from?: PlainDate) => scheduleFrom({
  anchor: v.date as PlainDate,
  repeat: v.repeat,
  end: v.end,
  ...(from ? { from } : {}),
});

export function ScheduleFields({ value, onChange, dateLabel = 'Дата платежа', repeatTip }: {
  value: ScheduleDraft;
  onChange: (v: ScheduleDraft) => void;
  dateLabel?: string;
  /** Пояснение к повторению: значок «i» у подписи «Повторение». */
  repeatTip?: ReactNode;
}) {
  const set = (patch: Partial<ScheduleDraft>) => onChange({ ...value, ...patch });
  const repeats = value.repeat !== 'never';
  const rule = isDate(value.date) ? repeatText(scheduleOf(value)) : null;
  // Окончание раньше самого платежа — не строгость ради строгости: такой ряд
  // пуст, и платёж исчез бы из календаря молча.
  const endTooEarly = value.end.kind === 'date' && isDate(value.end.date)
    && isDate(value.date) && value.end.date < value.date;

  return (
    <>
      <label>{dateLabel}
        <DateButton label={dateLabel} value={value.date} onChange={(d) => set({ date: d })} /></label>

      <label>Повторение
        {repeatTip ? <Tip>{repeatTip}</Tip> : null}
        <SelectField label="Повторение" value={value.repeat} onChange={(v) => set({ repeat: v as RepeatKey })}
          options={REPEAT_OPTIONS.map((o) => ({ id: o.key, title: o.label }))} /></label>

      {/* У разового платежа окончания нет: он случается один раз. */}
      {repeats && (
        <>
          <label>Когда закончится
            <SelectField label="Когда закончится" value={value.end.kind}
              onChange={(v) => {
                const kind = v as RepeatEnd['kind'];
                set({
                  end: kind === 'date'
                    ? { kind: 'date', date: value.date as PlainDate }
                    : kind === 'times'
                      ? { kind: 'times', times: 12 }
                      : { kind: 'never' },
                });
              }}
              options={[{ id: 'never', title: 'Никогда' }, { id: 'date', title: 'В дату' }, { id: 'times', title: 'После нескольких платежей' }]} /></label>

          {value.end.kind === 'date' && (
            <label>Последний платёж не позже
              <DateButton label="Последний платёж не позже" value={value.end.date} min={value.date || undefined}
                onChange={(d) => set({ end: { kind: 'date', date: d as PlainDate } })} /></label>
          )}
          {value.end.kind === 'times' && (
            <label>Сколько всего платежей
              <input value={String(value.end.times)} inputMode="numeric"
                onChange={(e) => set({
                  end: { kind: 'times', times: Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1) },
                })} /></label>
          )}
        </>
      )}

      {endTooEarly && (
        <div className="warnline bad">
          Окончание раньше самого платежа — тогда платежей не будет ни одного.
        </div>
      )}

      {/* Что получилось — словами. Иначе человек узнаёт правило только из
          календаря, где его уже поздно исправлять. */}
      {rule && !endTooEarly && (
        <div className="note">
          {value.repeat === 'never'
            ? 'Учту один раз: дальше этот платёж не появится.'
            // `repeatText` уже включает окончание: приписывать его второй раз
            // значило бы «ежемесячно, 3 платежа — 3 платежа».
            : `Буду считать ${rule}.`}
        </div>
      )}
    </>
  );
}

/** Повторы, которые бывают не каждый месяц: в остальные месяцы платёж не вычитается. */
const RARE: readonly RepeatKey[] = ['quarter', 'halfYear', 'year'];
const repeatTitle = (k: RepeatKey): string => REPEAT_OPTIONS.find((o) => o.key === k)?.label ?? '';

/**
 * Расписание строками — для листов «как у кредита» (22.10.2026).
 *
 * «Когда» — одна строка: дата и под ней повтор («дальше каждый месяц»). Тап —
 * свой лист: «Дата платежа» и «Повторять» строкой с коротким списком. Восемь
 * вариантов с пояснениями под каждым были шумом (замечание владельца).
 * «Когда закончится» — строкой ниже, только у повторяющегося.
 *
 * Строки ставятся внутрь карточки; ошибка и итог словами — `ScheduleNote`
 * под карточкой.
 */
export function ScheduleRows({ value, onChange, today, label = 'Когда' }: {
  value: ScheduleDraft;
  onChange: (v: ScheduleDraft) => void;
  today: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<ScheduleDraft>) => onChange({ ...value, ...patch });
  const repeats = value.repeat !== 'never';
  const text = isDate(value.date) ? dayMonth(value.date, today) : 'Выбрать';
  const sub = repeats ? `дальше ${repeatTitle(value.repeat).toLowerCase()}` : 'один раз';
  return (
    <>
      <div className={`rcselrow${isDate(value.date) ? '' : ' rcwarn'}`}>
        <span className="rclabel">{label}</span>
        <span className="rcvalue">
          <span className="rcvalcol">
            <span className={`rcvaltext${isDate(value.date) ? '' : ' rcnone'}`}>{text}</span>
            <small>{sub}</small>
          </span>
          <Icon name="chevron-right" size={14} className="rcchev" />
        </span>
        <button type="button" className="opover overbtn" aria-label={`${label}: ${text}, ${sub}`} onClick={() => setOpen(true)} />
      </div>
      {repeats && (
        <CardPick label="Когда закончится" value={value.end.kind}
          onChange={(v) => {
            const kind = v as RepeatEnd['kind'];
            set({
              end: kind === 'date'
                ? { kind: 'date', date: value.date as PlainDate }
                : kind === 'times'
                  ? { kind: 'times', times: 12 }
                  : { kind: 'never' },
            });
          }}
          options={[{ id: 'never', title: 'Никогда' }, { id: 'date', title: 'В дату' }, { id: 'times', title: 'После нескольких платежей' }]} />
      )}
      {repeats && value.end.kind === 'date' && (
        <CardDate label="Последний платёж не позже" value={value.end.date} {...(isDate(value.date) ? { min: value.date } : {})}
          text={isDate(value.end.date) ? dayMonth(value.end.date, today) : 'Выбрать'}
          onChange={(d) => set({ end: { kind: 'date', date: d as PlainDate } })} />
      )}
      {repeats && value.end.kind === 'times' && (
        <CardInput label="Сколько всего платежей" value={String(value.end.times)} inputMode="numeric" width={60}
          onChange={(x) => set({ end: { kind: 'times', times: Math.max(1, Number(x.replace(/\D/g, '')) || 1) } })} />
      )}
      {open && (
        <SheetWrap title={label} onClose={() => setOpen(false)} className="rcpt">
          <div className="rcform">
            <div className="rccard rcsel">
              <CardDate label="Дата платежа" value={value.date}
                text={isDate(value.date) ? dayMonth(value.date, today) : 'Выбрать'}
                onChange={(d) => set({ date: d })} />
              <CardPick label="Повторять" value={value.repeat} onChange={(v) => set({ repeat: v as RepeatKey })}
                options={REPEAT_OPTIONS.map((o) => ({ id: o.key, title: o.label }))} />
            </div>
            {RARE.includes(value.repeat) && (
              <p className="note">{repeatTitle(value.repeat)}: в остальные месяцы из расчёта не вычитается.</p>
            )}
            {value.repeat === 'never' && <p className="note">«Никогда» — платёж один раз.</p>}
            <button type="button" className="btn pri" disabled={!isDate(value.date)} onClick={() => setOpen(false)}>Готово</button>
          </div>
        </SheetWrap>
      )}
    </>
  );
}

/** Ошибка окончания и итог словами — под карточкой со строками расписания. */
export function ScheduleNote({ value }: { value: ScheduleDraft }) {
  const rule = isDate(value.date) ? repeatText(scheduleOf(value)) : null;
  const endTooEarly = value.end.kind === 'date' && isDate(value.end.date)
    && isDate(value.date) && value.end.date < value.date;
  if (endTooEarly) {
    return <div className="warnline bad">Окончание раньше самого платежа — тогда платежей не будет ни одного.</div>;
  }
  if (!rule) return null;
  return (
    <div className="note">
      {value.repeat === 'never' ? 'Учту один раз: дальше этот платёж не появится.' : `Буду считать ${rule}.`}
      {/* Главное, ради чего появилась периодичность: годовой платёж не
          вычитается из каждого месяца. */}
      {RARE.includes(value.repeat) && ' В остальные месяцы из расчёта не вычитается.'}
    </div>
  );
}
